/**
 * SkySystem - ceu procedural, atmosfera, environment map e neblina.
 *
 * Clima alvo: FIM DE TARDE. Sol baixo (15 graus), luz quente e rasante, ceu
 * quente perto do sol e frio no zenite, ar com particulado.
 *
 * Este modulo e a FONTE UNICA da atmosfera. Tudo que depende do clima sai daqui:
 *   - o ceu que se ve            -> mesh do addon Sky (modelo de Preetham)
 *   - a luz ambiente do PBR      -> environment map gerado por PMREMGenerator
 *   - a cor e a forca do sol     -> getSunColor() / getSunIntensity()
 *   - a neblina de distancia     -> scene.fog, com a cor tirada do MESMO modelo
 *
 * A neblina tem DOIS termos: a FogExp2 do three (curta e media distancia, dona da
 * legibilidade de alvo) e a perspectiva aerea de longa distancia com gradiente de
 * altura, injetada nos chunks de fog do three por _patchFogChunks(). O segundo
 * termo existe porque o skyline chegava ao horizonte com contraste quase integral,
 * como recorte de papelao, e subir a densidade global apagaria o alvo a 90 m.
 *
 * Por que uma porta do Preetham para a CPU (_evalSky):
 * a cor da neblina, a cor do sol e as cores do preenchimento hemisferico
 * PRECISAM casar com o ceu desenhado. Reimplementar o mesmo modelo em JS garante
 * isso por construcao, em vez de eu chutar cores "parecidas" na mao. Neblina que
 * nao casa com o horizonte e o erro mais denunciador do genero.
 *
 * TETO SUAVE (SKY_CAP_FACTOR): o Preetham cru entrega, no lobo de Mie em volta
 * do sol, radiancia acima de 15 - depois do ACES isso vira 255,255,239 em
 * centenas de pixels, sem gradiente e sem croma. O mesmo teto que ja existia so
 * na neblina agora e aplicado TAMBEM na saida do shader do ceu, e pela
 * LUMINANCIA (nao canal a canal), que e o que preserva a razao R:G:B do poente.
 * Sky e fog passam a ser o MESMO numero por construcao, inclusive contra o sol.
 *
 * Custo por frame: uma avaliacao do modelo (algumas dezenas de flops) e duas
 * copias de posicao. O environment map e gerado UMA vez em init(), e so e
 * refeito se o sol mudar (setSun).
 */

import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';

// --------------------------------------------------------------------------
// Clima. Numeros calibrados fora do jogo, avaliando o modelo de Preetham e
// aplicando ACES + sRGB, para que o resultado na tela caia na faixa certa com
// toneMappingExposure ~1.0. Nao mexer sem refazer a conta.
// --------------------------------------------------------------------------
const SUN_ELEVATION_DEG = 15.0;   // sombras ~3.7x a altura do objeto
const SUN_AZIMUTH_DEG = 118.0;    // sol no quadrante +X/-Z

/**
 * Particulado, azul do zenite e lobo frontal. Subiram (era 6.5 / 2.7 / 0.005)
 * porque o domo lia MEIO-DIA sobre uma sombra de fim de tarde: o gradiente
 * quente ficava numa faixa fina de horizonte e sumia na vista a altura do olho.
 *
 * Turbidity e mieCoefficient sao o que levanta o lobo quente do horizonte para
 * cima - com 8.5 / 0.006 o espalhamento de Mie ainda pesa em torno de 25-30
 * graus de elevacao no azimute do sol, e o azul fica no zenite oposto. Rayleigh
 * sobe junto para o zenite nao ficar leitoso com tanto particulado.
 *
 * O teto suave (SKY_CAP_FACTOR) e o que permite subir isso sem chapar: a
 * energia extra do lobo e comprimida pela LUMINANCIA, entao vira cor, nao
 * branco. Ao mesmo tempo o environment map, que sai deste mesmo ceu, fica mais
 * quente e mais forte - o que ajuda o rebalanco sol/ceu (ver ENVIRONMENT_INTENSITY).
 */
const TURBIDITY = 8.5;            // particulado no ar (halo quente em volta do sol)
const RAYLEIGH = 3.0;             // quanto o zenite puxa para o azul
const MIE_COEFFICIENT = 0.006;
const MIE_DIRECTIONAL_G = 0.80;   // espalhamento para a frente = brilho junto do sol

/**
 * Escala aplicada a saida do shader do ceu. O modelo de Preetham entrega
 * radiancia numa escala em que TUDO estoura depois do tone mapping. Sem esta
 * escala o ceu vira uma chapa branca e o environment map afoga o sol.
 */
const SKY_LUMINANCE = 0.28;

/**
 * Fracao do disco solar mantida. Cheio, o disco vale ~2.3e4 de radiancia (medido)
 * num punhado de pixels e vira combustivel de bloom que o PostFX nao tem como
 * segurar - bloom estourado e a assinatura de amador.
 *
 * O disco e retirado ANTES do teto suave e somado DEPOIS (ver SKY_TAIL_PATCH):
 * se passasse pelo teto sairia do outro lado com o mesmo valor do halo, ou seja,
 * invisivel - que e exatamente o que acontecia antes. Fora do teto ele e o unico
 * elemento cravado do quadro: a 0.0005 o nucleo vale ~11.5 de radiancia contra
 * 0.58 do halo comprimido em volta, um disco pequeno, definido e quente.
 *
 * CONFERIDO no three 0.185 instalado (node_modules/three/examples/jsm/objects/
 * Sky.js, uniforms 'showSunDisc': { value: 1 }, usado no fragmento em
 * `float sundisc = smoothstep(...) * showSunDisc;`): a uniform EXISTE e este
 * valor chega ao shader. A critica da rodada 2 afirma o contrario ("essa uniform
 * nao existe no Sky do three") - esta errada, e por isso o `if` que a protege
 * NAO foi removido: ele e so guarda de versao.
 */
const SUN_DISC = 0.0005;

/**
 * Peso do environment map na iluminacao ambiente do PBR.
 *
 * Historico: 0.40 -> 0.62 quando o teto suave tirou a energia leitosa do lado do
 * ceu virado para o sol (ai o ambiente virou cor de ceu em vez de leite branco).
 *
 * SUBIU DE 0.62 PARA 1.10 (rebalanco sol/ceu). Medido nos prints da rodada 2:
 * caixote de albedo escuro em sombra aberta caia para luminancia 3,6/255 (1,4%)
 * contra piso vizinho a 33,6 - 9x de diferenca entre materiais cujo albedo real
 * difere ~2x. Junto com a luminancia morria a CROMA: B-R caia de +27 (piso) para
 * +2,4 (caixote), ou seja a sombra azulada so sobrevivia em albedo claro.
 *
 * A causa era razao sol/ceu de ~15:1 num ceu de 15 graus de elevacao, onde a
 * extincao atmosferica coloca a razao fisica entre 2:1 e 4:1 - cerca de 2 stops
 * fora do horario que o modulo declara. O lado do ceu levava tres reducoes
 * empilhadas (SKY_LUMINANCE, este peso e HEMI_INTENSITY do Lighting).
 *
 * Aqui a correcao e do LADO DO CEU (o sol desce em SUN_ILLUMINANCE): 1.10 leva o
 * ambiente de uma parede vertical para a casa de 0.5-0.6 contra 3.2 do sol, ou
 * seja razao ~6:1, que e o alvo. A alta luz nao se move porque o ambiente entra
 * onde o sol nao chega.
 */
const ENVIRONMENT_INTENSITY = 1.10;

/**
 * SATURACAO DO CEU APENAS NA CAPTURA DO ENVIRONMENT MAP. O ceu que se ve na tela
 * continua em 1.0 - este valor entra por uniform so durante _buildEnvironment e
 * volta para 1.0 no finally, do mesmo jeito que o disco solar.
 *
 * Por que existe: subir ENVIRONMENT_INTENSITY para 1.10 resolveu o breu da
 * sombra (o objeto de albedo escuro em sombra aberta nao cai mais para 1,4% de
 * luminancia), mas subiu NIVEL e CROMA juntos - e a croma do domo e brutal.
 * Rodando o modelo de Preetham deste arquivo (turbidity 8.5, rayleigh 3.0, sol a
 * 15 graus), o zenite sai em radiancia (0.037, 0.119, 0.290): B/R = 7.8. Nem
 * mesmo a regiao do ceu virada para o sol e quente - a 45 graus de elevacao no
 * azimute do sol o modelo devolve (0.217, 0.502, 0.866) depois do teto suave,
 * ainda azul-dominante, porque o lobo quente do poente vive numa faixa fina de
 * horizonte. Ou seja: a irradiancia do domo inteiro e azul, e ela preenche TUDO
 * que nao ve o sol. Resultado medido nos prints da rodada 3: asfalto e parapeito
 * em sombra com B/R ~3.4 no estagio de luz, o que depois do split tone e do
 * ganho de saturacao na sombra do PostFX chega na tela como azul-ciano chapado -
 * a cor do material desaparecia sob a cor da luz.
 *
 * A correcao NAO e baixar o environment (isso traz o breu de volta): e tirar a
 * CROMA e deixar o NIVEL onde esta. A dessaturacao e feita em direcao ao cinza
 * de MESMA LUMINANCIA (mix com dot(rgb, luma)), portanto preserva a irradiancia
 * canal por canal na media e nao mexe um decimo na luminancia da sombra.
 *
 * 0.30 mantem 30% da croma do ceu: a luz de preenchimento continua mais fria que
 * o sol (1, 0.56, 0.26) por uma margem enorme, e a diferenca de temperatura
 * aparece onde tem que aparecer - na COMPARACAO entre area batida de sol e area
 * de sombra - em vez de virar tinta azul sobre o material. Custo assumido: o
 * reflexo especular do ceu em vidro e metal fica com 30% da croma (o env map e
 * um so para difuso e especular, e separar os dois exigiria mexer em
 * Materials.js, fora do escopo). Por isso nao vai a zero.
 */
const ENV_SKY_SATURATION = 0.30;

/** Irradiancia "equivalente branco" do sol. A intensidade real e derivada dela
 *  dividindo pela luminancia da cor do sol, entao mudar a elevacao nao muda o
 *  brilho aparente da cena, so a cor.
 *
 *  BAIXOU DE 4.5 PARA 3.2: a metade do rebalanco sol/ceu. Fechar a razao so
 *  subindo o ambiente empurraria o sol para o clipe (ele ja chegava perto);
 *  descendo o sol em ~1/3 de stop e subindo o ceu, a razao vai de ~15:1 para
 *  ~6:1 sem mexer na exposicao. */
const SUN_ILLUMINANCE = 3.2;

/** Quanto a cor do sol e clareada em direcao ao branco.
 *
 *  BAIXOU DE 0.26 PARA 0.18: com 26% de branco o sol saia lavado (1, 0.63,
 *  0.35) e o quadro perdia o ambar de golden hour que a sombra longa promete.
 *  Com a atmosfera mais espessa (TURBIDITY 8.5) a transmitancia crua ja e mais
 *  vermelha, entao 0.18 e o suficiente: a cor resultante e ~(1, 0.56, 0.26).
 *  Menos que isso (o 0.10 sugerido na critica) somado a nova turbidez daria sol
 *  quase vermelho puro, o que tinge concreto de laranja de filtro. */
const SUN_WHITE_MIX = 0.18;

/**
 * Densidade da neblina. Era 0.0068, que da 31% a 90 m: num mapa de 90 x 90 m isso
 * deixa o predio do outro extremo a 40 niveis do ceu (medido no print), ou seja
 * silhueta de alvo ilegivel. A 0.0032 sobra ar suficiente para separar os planos
 * (8% a 90 m) e o mesmo predio fica 112 niveis abaixo do ceu.
 * 1.6% a 40 m, 8% a 90 m, 20% a 150 m, 49% a 260 m.
 */
const FOG_DENSITY = 0.0032;

/**
 * PERSPECTIVA AEREA (termo de longa distancia, somado a FogExp2).
 *
 * Defeito medido na rodada 2: o anel de cenografia (74 a 179 m do centro) chega
 * ao horizonte com contraste quase integral - face em sombra a luminancia 66
 * contra ceu a 197, razao 3:1, silhueta navalhada de recorte de papelao. A
 * FogExp2 a 0.0032 entrega so 20% a 150 m, e 20% nao separa plano nenhum.
 *
 * Subir FOG_DENSITY nao resolve: a rodada anterior baixou de 0.0068 justamente
 * porque a 90 m o alvo virava silhueta ilegivel. Entao a densidade global fica
 * onde esta e a bruma extra so LIGA depois de AERIAL_START_M, com forma
 * gaussiana na distancia excedente:
 *
 *   extra(d) = 1 - exp( -( max(d - 70, 0) * 0.0068 )^2 )
 *
 *   d =  40 m   0%      (alvo perto: intocado)
 *   d =  90 m   1,8%    -> somado ao base da 9,6% contra 8,0% de hoje
 *   d = 120 m  10,9%    -> 23% combinado
 *   d = 179 m  42,3%    -> 57% combinado (a face de luminancia 66 sobe para a
 *                          faixa pedida sem tocar na legibilidade de alvo)
 *   d = 260 m  81%      -> 90% combinado, skyline dissolve no ceu
 */
const AERIAL_START_M = 70.0;
const AERIAL_DENSITY = 0.0068;

/**
 * GRADIENTE DE ALTURA da neblina. A FogExp2 e distancia pura: o topo de um
 * predio de 40 m recebe a mesma bruma que a base, o oposto do que o ar faz.
 *
 * A bruma e multiplicada por exp( -(y - 4) / 34 ), misturada em
 * FOG_HEIGHT_AMOUNT para nao apagar a neblina dos predios altos de vez:
 *   y <= 4 m  -> 100% da bruma (rua, onde o ar e mais denso)
 *   y = 20 m  ->  75%
 *   y = 40 m  ->  58%
 * O que da a leitura de ar: base lavada, topo mais limpo.
 */
const FOG_HEIGHT_START_M = 4.0;
const FOG_HEIGHT_SCALE_M = 34.0;
const FOG_HEIGHT_AMOUNT = 0.65;

/** Peso da media global do horizonte contra a amostra na direcao da vista.
 *  A neblina e direcional de proposito: olhando para o sol o ar espalha para a
 *  frente e clareia, olhando contra ele escurece. Isso e fisico, nao truque. */
const FOG_VIEW_MIX = 0.78;
const FOG_SAMPLE_ELEVATION_DEG = 4.0;
const FOG_LERP_PER_SECOND = 3.2;  // suaviza a troca de cor ao girar a camera

/**
 * Teto suave do ceu E da neblina (mesmo numero para os dois - e o que garante
 * que a neblina some na cor exata do ceu atras dela).
 *
 * Calibrado numericamente rodando o modelo inteiro ate o pixel (Preetham ->
 * teto -> exposicao -> ACES do three -> grade do PostFX -> sRGB), numa coluna de
 * ceu passando pelo sol:
 *   sem teto          -> R varia   5 niveis, R-B  16   (o defeito: chapa branca)
 *   teto por canal 3.2-> R varia   8 niveis, R-B  26   (ainda chapado)
 *   teto por lum 3.2  -> R varia  14 niveis, R-B  40
 *   teto por lum 1.25 -> R varia  42 niveis, R-B  72   <- este
 * Comprimir canal a canal empurra os tres canais para o mesmo teto e devolve
 * branco; por isso a compressao e da LUMINANCIA, com escala igual nos canais.
 */
const SKY_CAP_FACTOR = 1.25;

const SKY_RADIUS = 12000;
const CLOUD_RADIUS = 900;
const ENV_GROUND_RADIUS = 400;
const ENV_SIZE = 256;

/**
 * Passo, em uv da textura de ruido, usado para achar a BORDA da nuvem virada
 * para o sol. Uma amostra so, deslocada na direcao do sol projetada no plano.
 *
 * Calibrado medindo a propria textura gerada: com 0.010 a diferenca media de
 * densidade era 0.014 - so 11% dos pixels de nuvem acendiam, um fio de 1 px.
 * Com 0.032 a diferenca chega perto da largura da rampa da mascara e ~36% da
 * area de cada nuvem le como face iluminada (~9 px de rampa perto do horizonte),
 * que e o que da volume. Acima disso a "borda" invade o miolo.
 */
const CLOUD_SUN_STEP = 0.032;

/** Radiancia do casco de solo usado so na geracao do environment map.
 *  Sem ele, tudo que aponta para baixo recebe zero ambiente e fica preto -
 *  o mundo perde o rebatimento quente do asfalto iluminado. */
const GROUND_BOUNCE = [0.085, 0.065, 0.048];

// --------------------------------------------------------------------------
// Constantes do modelo de Preetham. Copiadas do shader do addon (Sky.js) para
// que a versao CPU devolva exatamente a mesma cor da versao GPU.
// --------------------------------------------------------------------------
const TOTAL_RAYLEIGH = [5.804542996261093e-6, 1.3562911419845635e-5, 3.0265902468824876e-5];
const MIE_CONST = [1.8399918514433978e14, 2.7798023919660528e14, 4.0790479543861094e14];
const CUTOFF_ANGLE = 1.6110731556870734;
const STEEPNESS = 1.5;
const SUN_EE = 1000.0;
const RAYLEIGH_ZENITH_LENGTH = 8.4e3;
const MIE_ZENITH_LENGTH = 1.25e3;
const THREE_OVER_SIXTEENPI = 0.05968310365946075;
const ONE_OVER_FOURPI = 0.07957747154594767;
const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

// --------------------------------------------------------------------------
// Temporarios de modulo. ZERO alocacao dentro de update().
// --------------------------------------------------------------------------
const _fwd = new THREE.Vector3();
const _rgb = { r: 0, g: 0, b: 0 };

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const luminanceOf = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/** Gerador deterministico simples (mulberry32). Ruido igual em toda maquina. */
function makeRng(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Ruido de valor com lattice que da a volta (size divisivel pela frequencia),
 * somado em oitavas. Gera textura de nuvem sem costura e sem asset externo.
 */
function fbmChannel(size, freqs, seed) {
  const out = new Float32Array(size * size);
  const rng = makeRng(seed);
  let amp = 0.5;
  for (let f = 0; f < freqs.length; f++) {
    const freq = freqs[f];
    const grid = new Float32Array(freq * freq);
    for (let i = 0; i < grid.length; i++) grid[i] = rng();
    const step = freq / size;
    for (let y = 0; y < size; y++) {
      const fy = y * step;
      const y0 = Math.floor(fy);
      const ty = fy - y0;
      const sy = ty * ty * (3 - 2 * ty);
      const y0i = y0 % freq;
      const y1i = (y0i + 1) % freq;
      const rowA = y0i * freq;
      const rowB = y1i * freq;
      for (let x = 0; x < size; x++) {
        const fx = x * step;
        const x0 = Math.floor(fx);
        const tx = fx - x0;
        const sx = tx * tx * (3 - 2 * tx);
        const x0i = x0 % freq;
        const x1i = (x0i + 1) % freq;
        const a = grid[rowA + x0i];
        const b = grid[rowA + x1i];
        const c = grid[rowB + x0i];
        const d = grid[rowB + x1i];
        const top = a + (b - a) * sx;
        const bot = c + (d - c) * sx;
        out[y * size + x] += (top + (bot - top) * sy) * amp;
      }
    }
    amp *= 0.5;
  }
  // normaliza para [0,1] para o limiar de cobertura ser previsivel
  let mn = Infinity;
  let mx = -Infinity;
  for (let i = 0; i < out.length; i++) {
    if (out[i] < mn) mn = out[i];
    if (out[i] > mx) mx = out[i];
  }
  const inv = mx > mn ? 1 / (mx - mn) : 1;
  for (let i = 0; i < out.length; i++) out[i] = (out[i] - mn) * inv;
  return out;
}

/**
 * Trecho que substitui a linha final do shader do addon (o marcador em
 * _buildSkyMesh). Roda no lugar de `gl_FragColor = vec4( texColor, 1.0 )` e usa
 * variaveis que ja existem no main() do addon: texColor, Fex, sundisc, vSunE.
 *
 * 1. Separa o disco solar do resto do ceu.
 * 2. Comprime SO o resto, pela luminancia, com o mesmo teto da neblina.
 * 3. Devolve o disco por cima, sem compressao nenhuma.
 * 4. Aplica a escala de luminancia global.
 *
 * Sem o passo 2 o halo de Mie crava em 255 num cone de ~25 graus em volta do
 * sol; sem o passo 3 o disco crava no MESMO valor do halo e some junto.
 *
 * 5. skySaturation e 1.0 em todo frame normal (o `if` garante saida bit a bit
 *    identica a de antes) e cai para ENV_SKY_SATURATION SO durante a captura do
 *    environment map. A dessaturacao e em direcao ao cinza de mesma luminancia,
 *    entao a irradiancia que preenche a sombra continua no mesmo NIVEL - muda so
 *    a croma, que era o que tingia tudo de azul-ciano.
 */
const SKY_TAIL_PATCH = /* glsl */`
	vec3 skyDisc = ( vSunE * 19000.0 * Fex ) * sundisc * 0.04;
	vec3 skyBody = max( texColor - skyDisc, vec3( 0.0 ) );
	float skyLum = dot( skyBody, vec3( 0.2126, 0.7152, 0.0722 ) );
	if ( skyLum > skyCap ) {
		float over = skyLum - skyCap;
		skyBody *= ( skyCap + over / ( 1.0 + over / ( skyCap * 0.9 ) ) ) / skyLum;
	}
	vec3 skyOut = ( skyBody + skyDisc ) * skyLuminance;
	if ( skySaturation < 1.0 ) {
		float outLum = dot( skyOut, vec3( 0.2126, 0.7152, 0.0722 ) );
		skyOut = mix( vec3( outLum ), skyOut, skySaturation );
	}
	gl_FragColor = vec4( skyOut, 1.0 );`;

/**
 * Remendo dos chunks de fog do three (perspectiva aerea + gradiente de altura).
 *
 * Por que remendar o chunk e nao trocar o material: a neblina tem que valer para
 * TODOS os materiais da cena (predio, chao, bot, particula), e eles nascem em
 * outros modulos - varios em tempo de jogo. O chunk e o unico ponto por onde
 * passa todo mundo. E o mesmo caminho que a critica sugeriu ("onBeforeCompile
 * leve no chunk de fog"), mas aplicado uma vez no ShaderChunk em vez de material
 * por material.
 *
 * vFogHeight (altura do fragmento no mundo) sai de mvPosition, que e a UNICA
 * variavel garantida em todos os shaders que incluem <fog_vertex> (mesh, points,
 * sprite e o shader de particula deste projeto). Como a rotacao de viewMatrix e
 * ortonormal, `v * mat3( viewMatrix )` e a transposta aplicada ao vetor, ou seja
 * a volta de view para mundo, sem precisar de inversa nem de varying nova no
 * vertex shader de ninguem.
 *
 * O remendo e guardado por marcador: se uma versao futura do three mudar o
 * chunk, nada e aplicado e a neblina volta a ser FogExp2 pura (avisa no console).
 */
const FOG_VARYING_MARKER = 'varying float vFogDepth;';
const FOG_VERTEX_MARKER = 'vFogDepth = - mvPosition.z;';
const FOG_MIX_MARKER = 'gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );';

const FOG_VARYING_PATCH = FOG_VARYING_MARKER + '\n\tvarying float vFogHeight;';
const FOG_VERTEX_PATCH = FOG_VERTEX_MARKER +
  '\n\tvFogHeight = ( mvPosition.xyz * mat3( viewMatrix ) + cameraPosition ).y;';

const FOG_MIX_PATCH = /* glsl */`float aerialD = max( vFogDepth - ${AERIAL_START_M.toFixed(1)}, 0.0 ) * ${AERIAL_DENSITY.toFixed(5)};
	float aerial = 1.0 - exp( - aerialD * aerialD );
	float heightFade = exp( - max( vFogHeight - ${FOG_HEIGHT_START_M.toFixed(1)}, 0.0 ) / ${FOG_HEIGHT_SCALE_M.toFixed(1)} );
	float fogTotal = 1.0 - ( 1.0 - fogFactor ) * ( 1.0 - aerial );
	fogTotal *= mix( 1.0, heightFade, ${FOG_HEIGHT_AMOUNT.toFixed(2)} );
	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, clamp( fogTotal, 0.0, 1.0 ) );`;

/** O ShaderChunk e global: remendar duas vezes duplicaria a declaracao da
 *  varying e nao compilaria. Uma instancia nova de SkySystem reaproveita. */
let fogChunksPatched = false;

const CLOUD_VERT = /* glsl */`
  varying vec3 vDir;
  void main() {
    vDir = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  }
`;

/**
 * Nuvens: uma camada fina projetada num plano de altitude, amostrada em TRES
 * escalas. Nucleo denso escurece (silhueta contra o ceu claro do poente) e a
 * BORDA virada para o sol recebe o contorno quente. Sutil de proposito - nuvem
 * chapada de desenho animado custa mais nota do que ceu limpo.
 *
 * Duas correcoes em cima da versao anterior, que so entregava borrao cinza:
 *
 * a) A rampa da mascara era 0.22 de largura sobre ruido de valor - isso nao
 *    produz aresta, produz degrade. A 0.07 a silhueta aparece; a terceira oitava
 *    (canal B da textura, antes zerado) da estrutura ao miolo em vez de liso.
 *
 * b) O contorno saia de pow(mu,5) puro, um lobo de ~40 graus centrado no sol:
 *    isso acende TODA a regiao do ceu perto do sol por igual, nao a borda de
 *    cada nuvem - por isso nenhuma nuvem tinha lado iluminado. Agora o ruido e
 *    amostrado UMA vez deslocado na direcao do sol (uSunStep) e acende onde o
 *    vizinho e mais fino que o pixel: isso e a borda voltada para o sol, e vale
 *    para qualquer nuvem do quadro. pow(mu,5) sobra so como espalhamento
 *    frontal, multiplicando a borda de quem esta perto do sol.
 */
const CLOUD_FRAG = /* glsl */`
  uniform sampler2D uNoise;
  uniform vec3 uSunDir;
  uniform vec3 uSunTint;
  uniform vec3 uBaseTint;
  uniform vec3 uShadeTint;
  uniform vec2 uSunStep;
  uniform float uTime;
  uniform float uCoverage;
  uniform float uScale;
  uniform float uOpacity;
  varying vec3 vDir;

  void main() {
    vec3 d = normalize( vDir );

    // projecao suave no plano das nuvens: sem singularidade no horizonte
    vec2 p = d.xz / ( d.y + 0.28 );
    vec2 uv1 = p * uScale + vec2( uTime * 0.0031, uTime * 0.0019 );
    vec2 uv2 = p * ( uScale * 2.7 ) + vec2( uTime * -0.0047, uTime * 0.0012 );
    vec2 uv3 = p * ( uScale * 6.1 ) + vec2( uTime * 0.0021, uTime * -0.0033 );

    float n1 = texture2D( uNoise, uv1 ).r;
    float n2 = texture2D( uNoise, uv2 ).g;
    float n3 = texture2D( uNoise, uv3 ).b;
    float n = n1 * 0.62 + n2 * 0.26 + n3 * 0.12;

    // rampa estreita = aresta. E a largura deste smoothstep que decide se a
    // nuvem tem silhueta ou vira vapor.
    float mask = smoothstep( uCoverage, uCoverage + 0.07, n );

    // faixa: nada colado no horizonte (onde a projecao estica) e rala no zenite
    float band = smoothstep( 0.045, 0.30, d.y ) * ( 1.0 - smoothstep( 0.62, 0.99, d.y ) * 0.55 );
    float alpha = mask * band * uOpacity;
    if ( alpha <= 0.002 ) discard;

    float thick = smoothstep( uCoverage + 0.01, uCoverage + 0.26, n );

    // borda virada para o sol: o vizinho UM PASSO na direcao do sol e mais fino
    // que aqui. E isto que acende a borda de CADA nuvem.
    float nSun = texture2D( uNoise, uv1 + uSunStep ).r;
    float edge = clamp( ( n1 - nSun ) * 9.0, 0.0, 1.0 );

    // o piso em 1e-4 evita pow(0.0, n) - definido pela norma, mas ha driver que
    // devolve NaN e um NaN aqui pinta a nuvem inteira de lixo
    float mu = clamp( dot( d, uSunDir ), 1e-4, 1.0 );
    float glow = pow( mu, 5.0 );

    // nucleo mais fechado que antes (0.48, era 0.62): sem lado escuro nao existe
    // lado claro - a nuvem so ganha volume no contraste entre os dois
    vec3 lit = mix( uBaseTint * 1.35, uBaseTint * 0.48, thick );
    vec3 shade = mix( uShadeTint * 1.10, uShadeTint * 0.42, thick );

    // LADO DO CEU. A nuvem no azimute do sol e cinza QUENTE; a do lado oposto e
    // azul-violeta e mais fechada. Antes as duas usavam o mesmo cinza frio: nuvem
    // branca e neutra nos dois lados le MEIO-DIA por si so, mesmo com sol a 15
    // graus e sombra longa no chao - era um dos sinais contraditorios da cena.
    float sunSide = smoothstep( -0.35, 0.65, dot( d, uSunDir ) );
    vec3 col = mix( shade, lit, sunSide );
    col += uSunTint * ( edge * ( 0.52 + 0.95 * glow ) + 0.16 * glow * ( 1.0 - thick ) );

    gl_FragColor = vec4( col, alpha );

    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export class SkySystem {
  constructor(ctx) {
    this.ctx = ctx;

    this.skyMesh = null;
    this.cloudMesh = null;
    this.envTarget = null;
    this.fog = null;

    /** Vetor unitario do mundo APONTANDO PARA O SOL (nao e a direcao da luz). */
    this.sunDirection = new THREE.Vector3();
    this.sunColor = new THREE.Color(1, 1, 1);
    this.sunIntensity = 1;
    this.fogColor = new THREE.Color(0.5, 0.5, 0.5);
    this.zenithColor = new THREE.Color(0.05, 0.12, 0.3);
    this.horizonColor = new THREE.Color(0.5, 0.5, 0.5);
    this.groundBounceColor = new THREE.Color(GROUND_BOUNCE[0], GROUND_BOUNCE[1], GROUND_BOUNCE[2]);

    /** Sobe a cada mudanca de sol. O Lighting observa isto para se realinhar. */
    this.sunVersion = 0;

    this._elevationDeg = SUN_ELEVATION_DEG;
    this._azimuthDeg = SUN_AZIMUTH_DEG;
    this._luminance = SKY_LUMINANCE;
    this._fogDensity = FOG_DENSITY;
    /** Teto suave em uso. Vira Infinity se o remendo do shader falhar, para o
     *  ceu (sem teto) e a neblina (na CPU) nao se separarem. */
    this._capFactor = SKY_CAP_FACTOR;
    this._time = 0;

    // coeficientes de espalhamento, recalculados so quando o sol muda
    this._betaR = [0, 0, 0];
    this._betaM = [0, 0, 0];
    this._sunE = 1;
    this._sunMix = 1;

    // cor global do horizonte (media em 32 azimutes), base da neblina
    this._fogBaseR = 0.5;
    this._fogBaseG = 0.5;
    this._fogBaseB = 0.5;

    this._pmrem = null;
    this._envScene = null;
    this._groundShell = null;
    this._cloudTexture = null;
    this._warned = false;
    this._ready = false;
  }

  async init() {
    const ctx = this.ctx;

    // ANTES de qualquer material compilar (o programa e montado no primeiro
    // render, e nada renderiza ainda nesta fase do boot). Se rodasse depois, os
    // materiais ja compilados ficariam sem a perspectiva aerea.
    this._patchFogChunks();

    this._buildSkyMesh();
    this._recomputeAtmosphere();

    // neblina exponencial com a cor do horizonte. FogExp2 e o que faz os planos
    // se separarem em profundidade sem "parede" de neblina linear.
    this.fog = new THREE.FogExp2(0x9aa5a8, this._fogDensity);
    this.fog.color.setRGB(this._fogBaseR, this._fogBaseG, this._fogBaseB);
    this.fogColor.copy(this.fog.color);
    ctx.scene.fog = this.fog;

    this._buildClouds();
    this._buildEnvironment();

    this._ready = true;
  }

  // ------------------------------------------------------------------------
  // Construcao
  // ------------------------------------------------------------------------

  /**
   * Injeta a perspectiva aerea e o gradiente de altura nos chunks de fog do
   * three. Roda UMA vez por processo (o ShaderChunk e global).
   *
   * Efeito colateral assumido: todo material com fog da cena passa a usar este
   * remendo. E o que se quer - neblina que valesse so para alguns objetos seria
   * pior que nenhuma. Materiais com `fog: false` (ceu, nuvem, casco de solo do
   * env map) nao definem USE_FOG e nao sao tocados.
   */
  _patchFogChunks() {
    if (fogChunksPatched) return;

    const chunks = THREE.ShaderChunk;
    const parsVert = chunks?.fog_pars_vertex;
    const vert = chunks?.fog_vertex;
    const parsFrag = chunks?.fog_pars_fragment;
    const frag = chunks?.fog_fragment;

    // Recarga a quente (vite HMR) reinicia o estado deste modulo, mas o modulo do
    // three continua em cache e JA remendado. Remendar de novo declararia a
    // varying duas vezes e nenhum shader compilaria - com tela preta e sem pista.
    if (typeof parsVert === 'string' && parsVert.indexOf('vFogHeight') !== -1) {
      fogChunksPatched = true;
      return;
    }

    const ok = typeof parsVert === 'string' && parsVert.indexOf(FOG_VARYING_MARKER) !== -1 &&
      typeof vert === 'string' && vert.indexOf(FOG_VERTEX_MARKER) !== -1 &&
      typeof parsFrag === 'string' && parsFrag.indexOf(FOG_VARYING_MARKER) !== -1 &&
      typeof frag === 'string' && frag.indexOf(FOG_MIX_MARKER) !== -1;

    if (!ok) {
      this._warnOnce('chunks de fog do three mudaram: perspectiva aerea desativada');
      return;
    }

    chunks.fog_pars_vertex = parsVert.replace(FOG_VARYING_MARKER, FOG_VARYING_PATCH);
    chunks.fog_vertex = vert.replace(FOG_VERTEX_MARKER, FOG_VERTEX_PATCH);
    chunks.fog_pars_fragment = parsFrag.replace(FOG_VARYING_MARKER, FOG_VARYING_PATCH);
    chunks.fog_fragment = frag.replace(FOG_MIX_MARKER, FOG_MIX_PATCH);
    fogChunksPatched = true;
  }

  _buildSkyMesh() {
    const sky = new Sky();
    sky.name = 'skyDome';
    sky.scale.setScalar(SKY_RADIUS);
    sky.frustumCulled = false;

    const mat = sky.material;
    mat.uniforms.turbidity.value = TURBIDITY;
    mat.uniforms.rayleigh.value = RAYLEIGH;
    mat.uniforms.mieCoefficient.value = MIE_COEFFICIENT;
    mat.uniforms.mieDirectionalG.value = MIE_DIRECTIONAL_G;
    // uniformes que so existem a partir do three 0.185 - guardados para nao
    // derrubar o boot se a versao mudar
    if (mat.uniforms.showSunDisc) mat.uniforms.showSunDisc.value = SUN_DISC;
    // as nuvens sao minha camada propria, com cor controlada; as do addon ficam
    // presas a uma formula que so devolve borrao cinza com sol baixo
    if (mat.uniforms.cloudCoverage) mat.uniforms.cloudCoverage.value = 0.0;
    if (mat.uniforms.cloudDensity) mat.uniforms.cloudDensity.value = 0.0;

    // Escala de luminancia E teto suave injetados no shader do addon. Se o
    // marcador mudar numa versao futura do three, o ceu continua funcionando
    // (sem escala e sem teto) e a versao CPU se ajusta sozinha via
    // this._luminance / this._capFactor, mantendo neblina e sol coerentes.
    const marker = 'gl_FragColor = vec4( texColor, 1.0 );';
    if (mat.fragmentShader.indexOf(marker) !== -1) {
      mat.uniforms.skyLuminance = { value: this._luminance };
      mat.uniforms.skyCap = { value: this._capFactor };
      // 1.0 em todo frame normal; cai para ENV_SKY_SATURATION so na captura do
      // environment map (ver _buildEnvironment)
      mat.uniforms.skySaturation = { value: 1.0 };
      mat.fragmentShader = 'uniform float skyLuminance;\nuniform float skyCap;\nuniform float skySaturation;\n' +
        mat.fragmentShader.replace(marker, SKY_TAIL_PATCH);
      mat.needsUpdate = true;
    } else {
      this._luminance = 1.0;
      this._capFactor = Infinity;
      this._warnOnce('nao foi possivel injetar a escala de luminancia no shader do ceu');
    }

    // Desenha primeiro e sem teste de profundidade: assim o ceu nunca ocupa
    // depth nem depende do truque de z=w do addon (que quebraria com depth
    // invertido). Mesmo custo de um scene.background.
    mat.depthTest = false;
    mat.depthWrite = false;
    sky.renderOrder = -1000;

    this.skyMesh = sky;
    this.ctx.scene.add(sky);
  }

  _buildClouds() {
    try {
      const size = 512;
      const chanR = fbmChannel(size, [4, 8, 16, 32, 64], 0x51f3a1);
      const chanG = fbmChannel(size, [8, 16, 32, 64], 0x9c1d77);
      // terceira oitava: e ela que da estrutura ao miolo da nuvem. O canal B
      // estava zerado e o resultado era nuvem lisa por dentro.
      const chanB = fbmChannel(size, [16, 32, 64, 128], 0x2b7ce1);
      const data = new Uint8Array(size * size * 4);
      for (let i = 0; i < size * size; i++) {
        const o = i * 4;
        data[o] = (chanR[i] * 255) | 0;
        data[o + 1] = (chanG[i] * 255) | 0;
        data[o + 2] = (chanB[i] * 255) | 0;
        data[o + 3] = 255;
      }
      const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.magFilter = THREE.LinearFilter;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.generateMipmaps = true;
      tex.anisotropy = Math.max(1, this.ctx.settings?.anisotropy ?? 4);
      tex.needsUpdate = true;
      this._cloudTexture = tex;

      const geo = new THREE.SphereGeometry(CLOUD_RADIUS, 48, 20, 0, Math.PI * 2, 0, Math.PI * 0.5);
      const mat = new THREE.ShaderMaterial({
        uniforms: {
          uNoise: { value: tex },
          uSunDir: { value: this.sunDirection.clone() },
          uSunTint: { value: new THREE.Color(1, 0.65, 0.38) },
          uBaseTint: { value: new THREE.Color(0.205, 0.168, 0.140) },
          uShadeTint: { value: new THREE.Color(0.118, 0.124, 0.170) },
          uSunStep: { value: new THREE.Vector2(CLOUD_SUN_STEP, 0) },
          uTime: { value: 0 },
          // 0.58 (era 0.56) porque a mascara estreitou: cada pixel coberto ficou
          // muito mais opaco, entao a mesma cobertura pesa mais na tela. Medido
          // na textura gerada: 27% do ceu com nuvem, 15% totalmente opaco.
          uCoverage: { value: 0.58 },
          // ~11 repeticoes da textura em volta do horizonte: massas grandes, sem
          // padrao visivel (o ruido nao tem forma reconhecivel para delatar o tile)
          uScale: { value: 1.2 },
          uOpacity: { value: 0.80 }
        },
        vertexShader: CLOUD_VERT,
        fragmentShader: CLOUD_FRAG,
        side: THREE.BackSide,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        fog: false
      });

      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = 'cloudLayer';
      mesh.frustumCulled = false;
      // primeiro dos transparentes: as nuvens ficam atras de vidro, particula e tracer
      mesh.renderOrder = -900;
      this.cloudMesh = mesh;
      this.ctx.scene.add(mesh);
      this._refreshCloudTints();
    } catch (err) {
      this._warnOnce('camada de nuvens desativada: ' + err);
      this.cloudMesh = null;
    }
  }

  /**
   * Environment map. E isto que da reflexo e luz de preenchimento corretos no
   * PBR - sem ele, metal fica preto e sombra fica chapada.
   *
   * A cena de captura tem o ceu SEM disco solar (o disco vira quadrado brilhante
   * de aliasing no cubemap de 256) e com a saturacao reduzida a
   * ENV_SKY_SATURATION, mais um casco de solo quente, para o hemisferio de baixo
   * nao ser preto.
   *
   * CONFERIDO (achado da rodada 3 - "o hemisferio inferior do PMREM ficou azul"):
   * o hemisferio de baixo NAO e azul. Ele nao vem do ceu, vem de _buildGroundShell,
   * que e MeshBasicMaterial com GROUND_BOUNCE = (0.085, 0.065, 0.048), ou seja
   * R/B = 1.77 - rebatimento QUENTE de asfalto batido de sol, que e justamente o
   * termo que contrabalanca o azul do domo numa parede vertical. O azul-ciano da
   * sombra vinha do hemisferio de CIMA (o domo de Preetham inteiro, nao so o
   * zenite) e e la que a dessaturacao age.
   */
  _buildEnvironment() {
    const ctx = this.ctx;
    const renderer = ctx.renderer;
    if (!renderer) {
      this._warnOnce('sem renderer: environment map nao gerado');
      return;
    }

    try {
      if (!this._pmrem) this._pmrem = new THREE.PMREMGenerator(renderer);
      if (!this._envScene) {
        this._envScene = new THREE.Scene();
        this._groundShell = this._buildGroundShell();
        this._envScene.add(this._groundShell);
      }

      const sky = this.skyMesh;
      const discUniform = sky.material.uniforms.showSunDisc;
      const disc = discUniform ? discUniform.value : 0;
      if (discUniform) discUniform.value = 0;

      /**
       * O ceu entra na captura DESSATURADO (ver ENV_SKY_SATURATION). E aqui, e
       * so aqui, que a cor do preenchimento de sombra e decidida: o que o PMREM
       * gravar nesta passada e o que vai preencher tudo que nao ve o sol.
       * A dessaturacao e de mesma luminancia, entao o NIVEL do preenchimento (o
       * que tira a sombra do breu) nao muda - muda so a croma.
       * O ceu visivel nao e afetado: a uniform volta a 1.0 no finally.
       */
      const satUniform = sky.material.uniforms.skySaturation;
      const sat = satUniform ? satUniform.value : 1.0;
      if (satUniform) satUniform.value = ENV_SKY_SATURATION;

      try {
        sky.position.set(0, 0, 0);
        this._envScene.add(sky);

        const previous = this.envTarget;
        this.envTarget = this._pmrem.fromScene(this._envScene, 0, 1, 2000, { size: ENV_SIZE });
        if (previous) previous.dispose();

        ctx.scene.environment = this.envTarget.texture;
        if ('environmentIntensity' in ctx.scene) ctx.scene.environmentIntensity = ENVIRONMENT_INTENSITY;
      } finally {
        // o ceu SEMPRE volta para a cena, com disco e saturacao originais, mesmo
        // se a captura falhar no meio
        ctx.scene.add(sky);
        if (discUniform) discUniform.value = disc;
        if (satUniform) satUniform.value = sat;
      }
    } catch (err) {
      this._warnOnce('falha ao gerar o environment map: ' + err);
    }
  }

  /** Meia esfera inferior com gradiente quente: o rebatimento do chao no env map. */
  _buildGroundShell() {
    const geo = new THREE.SphereGeometry(ENV_GROUND_RADIUS, 24, 12, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5);
    const pos = geo.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      // t = 0 junto do horizonte, 1 no nadir: o chao rebate mais perto do horizonte
      const t = clamp(-pos.getY(i) / ENV_GROUND_RADIUS, 0, 1);
      const k = 1 - 0.62 * t;
      colors[i * 3] = GROUND_BOUNCE[0] * k;
      colors[i * 3 + 1] = GROUND_BOUNCE[1] * k;
      colors[i * 3 + 2] = GROUND_BOUNCE[2] * k;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.BackSide,
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = -999;
    return mesh;
  }

  // ------------------------------------------------------------------------
  // Atmosfera derivada (roda so quando o sol muda)
  // ------------------------------------------------------------------------

  _recomputeAtmosphere() {
    const phi = (90 - this._elevationDeg) * DEG2RAD;
    const theta = this._azimuthDeg * DEG2RAD;
    const sp = Math.sin(phi);
    this.sunDirection.set(sp * Math.sin(theta), Math.cos(phi), sp * Math.cos(theta)).normalize();

    const sunY = clamp(this.sunDirection.y, -1, 1);
    this._sunE = SUN_EE * Math.max(0, 1 - Math.exp(-((CUTOFF_ANGLE - Math.acos(sunY)) / STEEPNESS)));
    this._sunMix = clamp(Math.pow(1 - sunY, 5), 0, 1);
    const mieC = 0.2 * TURBIDITY * 1e-17;
    for (let i = 0; i < 3; i++) {
      this._betaR[i] = TOTAL_RAYLEIGH[i] * RAYLEIGH;
      this._betaM[i] = 0.434 * mieC * MIE_CONST[i] * MIE_COEFFICIENT;
    }

    if (this.skyMesh) {
      const u = this.skyMesh.material.uniforms;
      u.sunPosition.value.copy(this.sunDirection);
      if (u.skyLuminance) u.skyLuminance.value = this._luminance;
      if (u.skyCap) u.skyCap.value = this._capFactor;
    }

    this._computeSunColor();
    this._computeReferenceColors();
    this._refreshCloudTints();
    this.sunVersion++;
  }

  /**
   * Cor do sol = transmitancia da atmosfera na direcao dele, normalizada e
   * clareada. Sai do mesmo modelo que desenha o ceu, entao nunca briga com ele.
   */
  _computeSunColor() {
    const dotUp = Math.max(0, this.sunDirection.y);
    const za = Math.acos(clamp(dotUp, -1, 1));
    const inv = 1 / (Math.cos(za) + 0.15 * Math.pow(93.885 - za * RAD2DEG, -1.253));
    const sR = RAYLEIGH_ZENITH_LENGTH * inv;
    const sM = MIE_ZENITH_LENGTH * inv;
    let r = Math.exp(-(this._betaR[0] * sR + this._betaM[0] * sM));
    let g = Math.exp(-(this._betaR[1] * sR + this._betaM[1] * sM));
    let b = Math.exp(-(this._betaR[2] * sR + this._betaM[2] * sM));
    const mx = Math.max(r, g, b, 1e-5);
    r /= mx; g /= mx; b /= mx;
    r += (1 - r) * SUN_WHITE_MIX;
    g += (1 - g) * SUN_WHITE_MIX;
    b += (1 - b) * SUN_WHITE_MIX;
    this.sunColor.setRGB(r, g, b);
    // intensidade compensa a cor: mudar a elevacao muda o tom, nao o brilho
    this.sunIntensity = SUN_ILLUMINANCE / Math.max(0.2, luminanceOf(r, g, b));
  }

  /** Zenite, horizonte medio e base da neblina. */
  _computeReferenceColors() {
    const cap = this._capFactor * this._luminance;

    this._evalSky(0, 1, 0, _rgb);
    this._capRadiance(_rgb, cap);
    this.zenithColor.setRGB(_rgb.r, _rgb.g, _rgb.b);

    let ar = 0;
    let ag = 0;
    let ab = 0;
    const N = 32;
    const elev = FOG_SAMPLE_ELEVATION_DEG + 1.5;
    const ce = Math.cos(elev * DEG2RAD);
    const se = Math.sin(elev * DEG2RAD);
    for (let i = 0; i < N; i++) {
      const a = (Math.PI * 2 * i) / N;
      this._evalSky(ce * Math.sin(a), se, ce * Math.cos(a), _rgb);
      this._capRadiance(_rgb, cap);
      ar += _rgb.r;
      ag += _rgb.g;
      ab += _rgb.b;
    }
    this._fogBaseR = ar / N;
    this._fogBaseG = ag / N;
    this._fogBaseB = ab / N;
    this.horizonColor.setRGB(this._fogBaseR, this._fogBaseG, this._fogBaseB);
  }

  _refreshCloudTints() {
    const mesh = this.cloudMesh;
    if (!mesh) return;
    const u = mesh.material.uniforms;
    u.uSunDir.value.copy(this.sunDirection);

    // passo da amostra de borda, na direcao do sol projetada no plano das nuvens
    const sx = this.sunDirection.x;
    const sz = this.sunDirection.z;
    const sl = Math.sqrt(sx * sx + sz * sz);
    const step = CLOUD_SUN_STEP * u.uScale.value;
    if (sl > 1e-5) u.uSunStep.value.set((sx / sl) * step, (sz / sl) * step);
    else u.uSunStep.value.set(step, 0);

    // Contorno quente, na escala de radiancia do ceu. Com o teto suave o ceu
    // junto do sol para em ~0.58; a face iluminada chega a ~0.6 longe do sol e
    // ~1.8 contra ele, o bastante para ler como lado aceso e para o bloom pegar
    // SO a borda - antes o ganho era 10x e o contorno estourava junto com o ceu,
    // sem desenhar nada.
    const k = 5.0 * this._luminance;
    u.uSunTint.value.setRGB(this.sunColor.r * k, this.sunColor.g * k, this.sunColor.b * k);
    // Corpo da nuvem em DOIS tons (era um cinza frio para os dois lados, o que
    // sozinho ja lia meio-dia): do lado do sol, cinza quente; do lado oposto,
    // azul-violeta um pouco mais fechado. O shader escolhe pelo angulo (sunSide).
    const b = this._luminance / SKY_LUMINANCE;
    u.uBaseTint.value.setRGB(0.205 * b, 0.168 * b, 0.140 * b);
    u.uShadeTint.value.setRGB(0.118 * b, 0.124 * b, 0.170 * b);
  }

  /** Teto suave escalar. Assintota em cap * 1.9 - nunca crava, so comprime. */
  _softCap(v, cap) {
    if (v <= cap) return v;
    return cap + (v - cap) / (1 + (v - cap) / (cap * 0.9));
  }

  /**
   * Aplica o teto na LUMINANCIA e devolve a mesma escala nos tres canais, no
   * lugar (zero alocacao). E o gemeo exato do que SKY_TAIL_PATCH faz na GPU -
   * por isso a neblina some na cor exata do ceu, inclusive contra o sol.
   *
   * Comprimir canal a canal (o obvio) levaria os tres ao mesmo teto e devolveria
   * branco chapado: medido, 8 niveis de variacao contra 42 desta versao.
   */
  _capRadiance(out, cap) {
    const l = luminanceOf(out.r, out.g, out.b);
    if (!(l > cap)) return out;
    const k = this._softCap(l, cap) / l;
    out.r *= k;
    out.g *= k;
    out.b *= k;
    return out;
  }

  // ------------------------------------------------------------------------
  // Modelo de Preetham na CPU (identico ao shader do addon)
  // ------------------------------------------------------------------------

  /** Escreve a radiancia linear do ceu na direcao (dx,dy,dz) em out {r,g,b}. */
  _evalSky(dx, dy, dz, out) {
    const dotUp = dy > 0 ? (dy > 1 ? 1 : dy) : 0;
    const za = Math.acos(dotUp);
    const inv = 1 / (Math.cos(za) + 0.15 * Math.pow(93.885 - za * RAD2DEG, -1.253));
    const sR = RAYLEIGH_ZENITH_LENGTH * inv;
    const sM = MIE_ZENITH_LENGTH * inv;

    const cosT = dx * this.sunDirection.x + dy * this.sunDirection.y + dz * this.sunDirection.z;
    const x = cosT * 0.5 + 0.5;
    const rPhase = THREE_OVER_SIXTEENPI * (1 + x * x);
    const g2 = MIE_DIRECTIONAL_G * MIE_DIRECTIONAL_G;
    let hg = 1 - 2 * MIE_DIRECTIONAL_G * cosT + g2;
    if (hg < 1e-4) hg = 1e-4;
    const mPhase = ONE_OVER_FOURPI * ((1 - g2) / Math.pow(hg, 1.5));

    const mixF = this._sunMix;
    const scale = 0.04 * this._luminance;

    for (let i = 0; i < 3; i++) {
      const betaR = this._betaR[i];
      const betaM = this._betaM[i];
      const Fex = Math.exp(-(betaR * sR + betaM * sM));
      const ratio = (betaR * rPhase + betaM * mPhase) / (betaR + betaM);
      let Lin = Math.pow(Math.max(0, this._sunE * ratio * (1 - Fex)), 1.5);
      Lin *= (1 - mixF) + mixF * Math.pow(Math.max(0, this._sunE * ratio * Fex), 0.5);
      const v = (Lin + 0.1 * Fex) * scale;
      if (i === 0) out.r = v;
      else if (i === 1) out.g = v + 0.0003 * this._luminance;
      else out.b = v + 0.00075 * this._luminance;
    }
    return out;
  }

  // ------------------------------------------------------------------------
  // Frame
  // ------------------------------------------------------------------------

  update(dt) {
    if (!this._ready) return;
    const camera = this.ctx.camera;
    if (!camera) return;

    // o ceu e as nuvens acompanham a camera: ficam sempre no infinito e a
    // direcao amostrada no shader e exata em qualquer ponto do mapa
    if (this.skyMesh) this.skyMesh.position.copy(camera.position);

    if (this.cloudMesh) {
      this.cloudMesh.position.copy(camera.position);
      this._time += dt;
      this.cloudMesh.material.uniforms.uTime.value = this._time;
    }

    this._updateFog(dt, camera);
  }

  /**
   * Neblina direcional: a cor vem do ceu no horizonte NA DIRECAO EM QUE SE OLHA,
   * misturada com a media global e amortecida no tempo. Assim o predio distante
   * sempre desaparece na cor exata do ceu logo atras dele - e olhar contra o sol
   * clareia o ar, como na vida real.
   */
  _updateFog(dt, camera) {
    const fog = this.fog;
    if (!fog) return;

    camera.getWorldDirection(_fwd);
    _fwd.y = 0;
    const len = _fwd.length();
    if (len > 1e-4) _fwd.multiplyScalar(1 / len);
    else _fwd.set(0, 0, -1);

    const ce = Math.cos(FOG_SAMPLE_ELEVATION_DEG * DEG2RAD);
    const se = Math.sin(FOG_SAMPLE_ELEVATION_DEG * DEG2RAD);
    this._evalSky(_fwd.x * ce, se, _fwd.z * ce, _rgb);
    this._capRadiance(_rgb, this._capFactor * this._luminance);

    const w = FOG_VIEW_MIX;
    const iw = 1 - w;
    const tr = _rgb.r * w + this._fogBaseR * iw;
    const tg = _rgb.g * w + this._fogBaseG * iw;
    const tb = _rgb.b * w + this._fogBaseB * iw;

    const k = 1 - Math.exp(-FOG_LERP_PER_SECOND * dt);
    const c = fog.color;
    c.r += (tr - c.r) * k;
    c.g += (tg - c.g) * k;
    c.b += (tb - c.b) * k;
    this.fogColor.copy(c);
  }

  // ------------------------------------------------------------------------
  // API publica
  // ------------------------------------------------------------------------

  /** Vetor unitario apontando PARA o sol. Nao mutar - e a instancia interna. */
  getSunDirection() {
    return this.sunDirection;
  }

  /** Cor linear do sol (transmitancia atmosferica normalizada). Nao mutar. */
  getSunColor() {
    return this.sunColor;
  }

  /** Intensidade sugerida para a DirectionalLight, coerente com o environment. */
  getSunIntensity() {
    return this.sunIntensity;
  }

  getFogColor() {
    return this.fogColor;
  }

  /** Cor media do horizonte (referencia estavel, nao muda com a camera). */
  getHorizonColor() {
    return this.horizonColor;
  }

  getZenithColor() {
    return this.zenithColor;
  }

  /** Cor do rebatimento do chao usada no environment map. */
  getGroundBounceColor() {
    return this.groundBounceColor;
  }

  getSunElevation() {
    return this._elevationDeg * DEG2RAD;
  }

  getEnvironmentTexture() {
    return this.envTarget ? this.envTarget.texture : null;
  }

  /** Amostra o modelo do ceu numa direcao qualquer, JA com o teto suave - ou
   *  seja, a cor que esta na tela. dir: Vector3 normalizado. */
  sampleSky(dir, out) {
    const target = out || new THREE.Color();
    this._evalSky(dir.x, dir.y, dir.z, _rgb);
    this._capRadiance(_rgb, this._capFactor * this._luminance);
    target.setRGB(_rgb.r, _rgb.g, _rgb.b);
    return target;
  }

  /** Move o sol e REGENERA o environment map. Caro - nunca chamar por frame. */
  setSun(elevationDeg, azimuthDeg) {
    this._elevationDeg = clamp(elevationDeg, 0.5, 89);
    this._azimuthDeg = azimuthDeg;
    this._recomputeAtmosphere();
    if (this.fog) {
      this.fog.color.setRGB(this._fogBaseR, this._fogBaseG, this._fogBaseB);
      this.fogColor.copy(this.fog.color);
    }
    this._buildEnvironment();
  }

  /** Escala global do brilho do ceu. Regenera o environment map. */
  setLuminance(value) {
    this._luminance = Math.max(0.01, value);
    this._recomputeAtmosphere();
    this._buildEnvironment();
  }

  setFogDensity(value) {
    this._fogDensity = Math.max(0, value);
    if (this.fog) this.fog.density = this._fogDensity;
  }

  setCloudCoverage(value) {
    if (this.cloudMesh) this.cloudMesh.material.uniforms.uCoverage.value = clamp(1 - value, 0.05, 0.95);
  }

  _warnOnce(msg) {
    if (this._warned) return;
    this._warned = true;
    console.warn('[Sky] ' + msg);
  }

  dispose() {
    const scene = this.ctx.scene;
    if (this.skyMesh) {
      scene.remove(this.skyMesh);
      this.skyMesh.geometry.dispose();
      this.skyMesh.material.dispose();
      this.skyMesh = null;
    }
    if (this.cloudMesh) {
      scene.remove(this.cloudMesh);
      this.cloudMesh.geometry.dispose();
      this.cloudMesh.material.dispose();
      this.cloudMesh = null;
    }
    if (this._cloudTexture) {
      this._cloudTexture.dispose();
      this._cloudTexture = null;
    }
    if (this._groundShell) {
      this._groundShell.geometry.dispose();
      this._groundShell.material.dispose();
      this._groundShell = null;
    }
    if (this.envTarget) {
      this.envTarget.dispose();
      this.envTarget = null;
    }
    if (this._pmrem) {
      this._pmrem.dispose();
      this._pmrem = null;
    }
    if (scene.environment) scene.environment = null;
    scene.fog = null;
    this.fog = null;
    this._ready = false;
  }
}
